import t from './i18n';

export function ready(cb) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cb);
  } else {
    cb();
  }
}

export const isArray = Array.isArray;

export function isEmpty(value) {
  if (
    isUndefined(value) ||
    value === null ||
    value === false ||
    value === 0
  ) {
    return true;
  }
  if (isArray(value) || isString(value)) {
    return value.length === 0;
  }
  if (isObject(value)) {
    return value.constructor === Object && Object.keys(value).length === 0;
  }

  return false;
}

export function isObject(value) {
  return typeof value === 'object';
}

export function isString(value) {
  return typeof value === 'string';
}

export function isUndefined(value) {
  return typeof value === 'undefined';
}

export function isProductOrderable(product) {
  return product?.isOrderable && product?.estimatedShipDate;
}

export function isCardExpired(paymentDetail) {
  if (!paymentDetail) return true;

  const today = new Date();
  let expiration = new Date().setFullYear(paymentDetail.expiryYear, paymentDetail.expiryMonth, 1);

  return expiration < today;
}

export function indexOf(el) {
  return Array.from(el.parentNode.children).indexOf(el);
}

export function getLangcode(langcode) {
  return langcode || 'en';
}

export function getDocumentLang() {
  return document.documentElement.lang.toLowerCase().replace('-', '_');
}

export function convertNameToPath(name) {
  return name
    .toLowerCase()
    .split(' ')
    .join('-')
    .split('/')
    .join('-')
    .replace(/\(/g, '')
    .replace(/\)/g, '')
    .replace('®', '');
}

export function kebabCase(str) {
  return str
    .replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, (_, a, b) =>
      a ? `${a}-${b}` : b
    )
    .toLowerCase();
}

export function getBaseName(fileName) {
  return fileName
    .split('/')
    .pop()
    .replace(/\.\w+$/, '');
}

export function getComponentName(fileName) {
  return kebabCase(getBaseName(fileName));
}

export function getImageLibraryUrl(mid) {
  const langcode = getDocumentLang();

  return `/${langcode === 'en' ? '' : langcode + '/'}`
    + `resources/downloads/sloan-image-library/${mid}`;
}

export const getImagesArray = (function() {
  const cache = {};
  return s => {
    if (isArray(s)) return s;
    if (cache[s]) return cache[s];
    try {
      cache[s] = JSON.parse(s || '[]');
    } catch (e) {
      cache[s] = [];
    }
    return cache[s];
  };
})();

export function getSpecSheetPath(sku, langcode) {
  let langPrefix = langcode;

  if (langcode === 'en') {
    langPrefix = false;
  }

  return `${langPrefix ? `/${langPrefix}` : ''}/spec-sheet/${sku}`;
}

export function getProductThumbnail(images) {
  if (images) {
    const thumbnail = images.filter(image => image.format === 'product')[0];
    if (thumbnail) {
      return [{src: thumbnail.url, alt: thumbnail.altText}];
    }
  }
  return '';
}

export function getCardNumberLastFourDigits(cardNumber) {
  if (!cardNumber) return '';

  return cardNumber.split('-')[2];
}

export function getCardTypeIconName(type) {
  switch (type) {
    case 'Visa':
      return 'CCVisa';
    case 'Mastercard':
      return 'CCMastercard';
    case 'American Express':
      return 'CCAmex';
    default:
      return '';
  }
}

export function formatSingleLineAddress(address) {
  if (!address) return '';

  return `${address?.line1 ? address.line1 + ', ' : ''}` +
    `${address?.line2 ? address.line2 + ', ' : ''}` +
    `${address?.town ? address.town + ', ' : ''}` +
    `${address?.region?.isocodeShort ? address?.region?.isocodeShort + ', ' : ''}` +
    `${address?.postalCode ? address.postalCode + ', ' : ''}` +
    `${address?.country?.isocode ? address?.country?.isocode: ''}`;
}

export function calculateAndFormatPrice(price, quantity) {
  if (!price || !quantity) return '';

  return `$${(price * quantity).toFixed(2).toLocaleString()}`;
}

export function formatOrderNumber(number) {
  if (!number) return '';

  return parseInt(number, 10);
}

export function handleApiErrors(e, context, fallback = '') {
  const errors = e?.response?.data?.errors ?? [];

  if (errors[0]) {
    if (errors[0]?.message === 'No orderable unit found for this product!') {
      context.$snotify.error(t('Product is not orderable at this time'));
      return;
    }
    if (errors[0]?.message === 'Cart not found.') {
      window.location = '/marketplace/cart';
      context.$snotify.error(t('Cart not found.'));
      return;
    }
  }

  if (errors.length >= 1 && errors[0]?.message) {
    context.$snotify.error(t(e?.response?.data?.errors[0]?.message));
  } else {
    context.$snotify.error(t(fallback));
  }
}

export function marketingEmailSignup(data) {
  const formId = document.head.querySelector('meta[name="marketo-marketplace-form-id"]').content;

  // eslint-disable-next-line no-undef
  MktoForms2.loadForm('//info.sloan.com', '905-IOZ-761', formId, function(form) {
    let fields = {'Email': data?.email};
    if (data?.firstName) fields['FirstName'] = data.firstName;
    if (data?.lastName) fields['LastName'] = data.lastName;

    form.vals(fields);
    form.submit();
    form.onSuccess(() => {
      return false;
    });
  });
}

export function tagManagerTrack(event, eventModel = {}) {
  // eslint-disable-next-line no-undef
  gtag('event', event, eventModel);
}

export function findCartEntry(cartEntries = [], sku = '') {
  return cartEntries.find(entry => entry.product?.code === sku);
}

export function getClickwrapAccessId() {
  return document.head.querySelector('meta[name="clickwrap-access-id"]').content;
}

export function initializeClickwrap(groupId, container) {
  window._ps = null;
  if (!window.navigator.webdriver) {
    // eslint-disable-next-line
    (function(w,d,s,c,f,n,t,g,a,b,l){w['PactSafeObject']=n;w[n]=w[n]||function(){(w[n].q=w[n].q||[]).push(arguments)},w[n].on=function(){(w[n].e=w[n].e||[]).push(arguments)},w[n].once=function(){(w[n].eo=w[n].eo||[]).push(arguments)},w[n].off=function(){(w[n].o=w[n].o||[]).push(arguments)},w[n].t=1*new Date(),w[n].l=0;a=d.createElement(s);b=d.getElementsByTagName(s)[0];a.async=1;a.src=c;a.onload=a.onreadystatechange=function(){w[n].l=1};a.onerror=a.onabort=function(){w[n].l=0};b.parentNode.insertBefore(a,b);setTimeout(function(){if(!w[n].l&&!w[n].loaded){w[n].error=1;a=d.createElement(s);a.async=1;a.src=f;a.onload=a.onreadystatechange=function(){w[n].l=1};a.onerror=a.onabort=function(){w[n].l=0};b.parentNode.insertBefore(a,b);l=function(u,e){try{e=d.createElement('img');e.src='https://d3r8bdci515tjv.cloudfront.net/error.gif?t='+w[n].t+'&u='+encodeURIComponent(u);d.getElementsByTagName('body')[0].appendChild(e)}catch(x){}};l(c);setTimeout(function(){if(!w[n].l&&!w[n].loaded){w[n].error=1;if(g&&'function'==typeof g){g.call(this);}l(f)}},t)}},t)})(window,document,'script','https://vault.pactsafe.io/ps.min.js','https://d3l1mqnl5xpsuc.cloudfront.net/ps.min.js','_ps',4000);

    window._ps('create', getClickwrapAccessId());
    window._ps('load', groupId, {
      container_selector: container,
      signer_id_selector: 'email'
    });
  }
}
